/* The slot registry: every image and video on the site that an administrator
   can replace from /admin, in one place.
 
   Adding a new editable slot is a two-step job:
     1. add an entry here with a stable key that never changes
     2. call useVideo(key, fallback) or useImage(key, fallback) at the point of use
 
   The key is what the Firestore document stores, so renaming a key orphans
   whatever was set against the old one. Labels and groups are display only
   and safe to reword at any time. */

export const videoSlots = [
  {
    key: 'practicums.marketing',
    group: 'Practicums',
    label: 'Hero film',
    help: 'The short marketing film at the top of the Practicums page.',
  },
  {
    key: 'practicums.fullCase',
    group: 'Practicums',
    label: 'Full encounter',
    help: 'The complete station walkthrough, shown under Anatomy of an encounter.',
  },
  {
    key: 'practicums.caseBuilding',
    group: 'Practicums',
    label: 'Building a case',
    help: 'The faculty authoring walkthrough in the programs section.',
  },
  {
    key: 'suites.pipeline',
    group: 'EdAI Suites',
    label: 'Item pipeline film',
    help: 'Designated source to approved item: generation, triage, review, audit trail.',
  },
  {
    key: 'medschool.prerounds',
    group: 'EdAI MedSchool',
    label: 'PreRounds walkthrough',
    help: 'A student case workup, the reconciliation, and the competency record it writes.',
  },
  {
    key: 'prep.session',
    group: 'EdAI Prep',
    label: 'Study session film',
    help: 'The adaptive plan, a sourced clinical case, and the review flow.',
  },
  {
    key: 'pathways.compare',
    group: 'Pathways',
    label: 'Route comparison film',
    help: 'A profile, the side-by-side comparison, and a committed step plan.',
  },
  {
    key: 'express.upload',
    group: 'EdAI Express',
    label: 'Upload walkthrough',
    help: 'A lecture PDF in, and the questions, guide, and Anki export it produces.',
  },
];

export const imageSlots = [
  {
    key: 'suites.generate',
    group: 'EdAI Suites',
    label: 'Item generation',
    fallback: '/screenshots/generate-mcq.png',
  },
  {
    key: 'suites.itemBank',
    group: 'EdAI Suites',
    label: 'Item bank',
    fallback: '/screenshots/item_bank.png',
  },
  {
    key: 'suites.orals',
    group: 'EdAI Suites',
    label: 'Oral examination preparation',
    fallback: '/screenshots/mod2-orals-prep.png',
  },
  {
    key: 'suites.lms',
    group: 'EdAI Suites',
    label: 'Learning management',
    fallback: '/screenshots/lms1.png',
  },
  {
    key: 'suites.factCheck',
    group: 'EdAI Suites',
    label: 'Fact checking',
    fallback: '/screenshots/mod2-fact-checker.png',
  },
  {
    key: 'suites.analytics',
    group: 'EdAI Suites',
    label: 'Program analytics',
    fallback: '/screenshots/analyze-dashboard.png',
  },
  {
    key: 'suites.proctoring',
    group: 'EdAI Suites',
    label: 'Examination administration',
    fallback: '/screenshots/administer-proctoring.png',
  },
  {
    key: 'medschool.prerounds',
    group: 'EdAI MedSchool',
    label: 'PreRounds case workup',
    fallback: '/screenshots/prerounds.jpg',
  },
  {
    key: 'medschool.dashboard',
    group: 'EdAI MedSchool',
    label: 'Student dashboard',
    fallback: '/screenshots/pre-dash.jpg',
  },
  {
    key: 'medschool.aiUse',
    group: 'EdAI MedSchool',
    label: 'Clinical AI use, captured',
    fallback: '/screenshots/pre-aiter.jpg',
  },
];

export const videoGroups = [...new Set(videoSlots.map((s) => s.group))];
export const imageGroups = [...new Set(imageSlots.map((s) => s.group))];

/* Accepts whatever an administrator pastes and returns a privacy-friendly
   embed URL, or null when the input is not a recognizable YouTube or Vimeo
   reference. Handles watch links, share links, shorts, live, already-embedded
   URLs, and bare video ids. */
export function toEmbedUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return null;
  if (/^[\w-]{11}$/.test(raw)) return `https://www.youtube-nocookie.com/embed/${raw}`;

  let url;
  try {
    url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.slice(1);
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
    const v = url.searchParams.get('v');
    if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
    const m = url.pathname.match(/\/(embed|shorts|live|v)\/([\w-]+)/);
    if (m) return `https://www.youtube-nocookie.com/embed/${m[2]}`;
    return null;
  }
  if (host.endsWith('vimeo.com')) {
    const id = url.pathname.split('/').filter(Boolean).pop();
    return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
  }
  return null;
}
